// App-icon badge math for the native mobile clients.
//
// APNs (and FCM's notification_count) set the springboard badge to an ABSOLUTE
// number, not a delta — so every push has to carry the recipient's *current*
// unread total or the icon shows nothing at all. Until this module existed no
// caller ever set `badge`, which is why the DashAlign icon never showed the red
// bubble even though the banner and the in-app nav badge both worked.
//
// Two rules shape the count:
//
//  1. Only conversations the recipient's NATIVE surface can actually open are
//     counted. On the phone, `surface='mobile'` scheduling threads open for
//     every role (/schedules/messages) while shared threads (/messages) are
//     admin/super_admin-only — see the surface split in send-message-push.
//     Counting a thread they cannot open would inflate a badge that they have
//     no way to clear.
//  2. A message the recipient sent themselves is never unread. This mirrors
//     public.get_unread_summary (migration 20260601000001), which is what the
//     in-app badges read, so the icon and the nav badge agree.
//
// Kept pure (rows in, counts out) so it can be unit-tested from
// src/lib/__tests__ — edge functions themselves have no coverage.

/** Roles whose native app has the full /messages surface. Mirrors send-message-push. */
export const NATIVE_MESSAGE_ROLES: readonly string[] = ['admin', 'super_admin'];

/**
 * iOS renders any badge over three digits as an ellipsised blob, and a count
 * that large is noise anyway. Cap it — the in-app badges cap at "99+" too.
 */
export const MAX_BADGE = 99;

export interface BadgeMembershipRow {
  profile_id: string;
  conversation_id: string;
  last_read_at: string | null;
}

export interface BadgeMessageRow {
  conversation_id: string;
  sender_id: string;
  created_at: string;
}

/**
 * Can this role open this conversation from the native app? `surface` is
 * conversations.surface ('mobile' for the scheduling inbox, null/other for the
 * shared messenger).
 */
export function isNativeOpenable(surface: string | null | undefined, role: string | undefined): boolean {
  if (surface === 'mobile') return true;
  return NATIVE_MESSAGE_ROLES.includes(role ?? '');
}

/**
 * Per-recipient unread totals, ready to drop into an APNs `badge`.
 *
 * `messages` is expected to be a bounded window (the caller fetches everything
 * newer than the earliest last_read_at in play, with a row cap). A window that
 * clips old history undercounts rather than overcounts, which is the safe
 * direction: the badge reads low for a moment and the next read clears it.
 *
 * Every id in `recipientIds` gets an entry — including 0 — so a push can also
 * correct a badge downward.
 */
export function computeUnreadBadges(input: {
  recipientIds: string[];
  roleByProfileId: Record<string, string | undefined>;
  memberships: BadgeMembershipRow[];
  surfaceByConversationId: Record<string, string | null | undefined>;
  messages: BadgeMessageRow[];
}): Record<string, number> {
  const { recipientIds, roleByProfileId, memberships, surfaceByConversationId, messages } = input;

  const badges: Record<string, number> = {};
  for (const id of recipientIds) badges[id] = 0;

  const byConversation = new Map<string, BadgeMessageRow[]>();
  for (const m of messages) {
    const bucket = byConversation.get(m.conversation_id);
    if (bucket) bucket.push(m);
    else byConversation.set(m.conversation_id, [m]);
  }

  for (const cm of memberships) {
    if (!(cm.profile_id in badges)) continue;
    if (!isNativeOpenable(surfaceByConversationId[cm.conversation_id], roleByProfileId[cm.profile_id])) {
      continue;
    }

    // A never-read thread has last_read_at NULL: everything in the window counts.
    const readAt = cm.last_read_at ? Date.parse(cm.last_read_at) : Number.NEGATIVE_INFINITY;
    const bucket = byConversation.get(cm.conversation_id);
    if (!bucket) continue;

    for (const msg of bucket) {
      if (msg.sender_id === cm.profile_id) continue;
      const sentAt = Date.parse(msg.created_at);
      if (!Number.isFinite(sentAt)) continue;
      if (sentAt > readAt) badges[cm.profile_id] += 1;
    }
  }

  for (const id of Object.keys(badges)) {
    if (badges[id] > MAX_BADGE) badges[id] = MAX_BADGE;
  }
  return badges;
}
